import * as Sentry from "@sentry/react";

/**
 * Bounded triage logging for the @nudge-ai/audio-store local audio backup.
 *
 * Design rule: NEVER flood. A recording emits a chunk every ~10s for up to 2-3h,
 * and a wedged storage tier can emit an `error` connection event per chunk, so
 * everything here is rate-limited (at most once per window per key). Sentry
 * breadcrumbs are cheap (session-local, only sent with a captured exception) and
 * are the primary "is local storage healthy?" signal. Fully defensive — logging
 * must never get in the way of the critical recording / GCS path.
 */

const lastAt = new Map<string, number>();
const THROTTLE_MS = 30_000;

function throttled(key: string, fn: () => void): void {
  const now = Date.now();
  if (now - (lastAt.get(key) ?? 0) < THROTTLE_MS) return;
  lastAt.set(key, now);
  fn();
}

/**
 * Log an audio-store connection-state change (open/closed/closing/blocked/
 * recovered/error). Throttled to once per 30s per state+engine so an error storm
 * cannot flood. `error`/`blocked` also warn to the console.
 */
export function logAudioStoreConnection(event: {
  state: string;
  engine?: string;
  reason?: string;
  error?: Error;
}): void {
  try {
    const key = `${event.state}:${event.engine ?? ""}`;
    throttled(key, () => {
      const level = event.state === "error" ? "error" : event.state === "blocked" ? "warning" : "info";
      Sentry.addBreadcrumb({
        category: "audio-store",
        level,
        message: `connection ${event.state}${event.engine ? ` (${event.engine})` : ""}`,
        data: { reason: event.reason, error: event.error?.message },
      });
      if (level !== "info") {
        console.warn(
          `[audio-store] connection ${event.state}${event.engine ? ` (${event.engine})` : ""}`,
          event.reason ?? "",
        );
      }
    });
  } catch {
    // logging must never throw into a storage operation
  }
}

/**
 * Local-backup write failure (the store rejected on every tier). Bounded to a
 * few Sentry captures per minute so a fully-broken-storage session can't flood;
 * after the cap, a single suppression breadcrumb. Fully defensive.
 */
let failWindowStart = 0;
let failCount = 0;
export function logAudioStoreSaveFailure(error: unknown, data: Record<string, unknown>): void {
  try {
    const now = Date.now();
    if (now - failWindowStart > 60_000) {
      failWindowStart = now;
      failCount = 0;
    }
    failCount += 1;
    if (failCount <= 3) {
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        extra: { ...data, failCount, context: "audio-store local save failed on all tiers" },
      });
    } else if (failCount === 4) {
      Sentry.addBreadcrumb({
        category: "audio-store",
        level: "warning",
        message: "local save failing repeatedly; suppressing further captures this minute",
        data,
      });
    }
  } catch {
    // logging must never throw into a storage operation
  }
}
